/**
 * usePomodoroTick - React hook that manages the Pomodoro timer interval.
 *
 * This hook runs the 1-second tick for the Pomodoro timer at the app shell level,
 * ensuring the timer continues regardless of which module is active.
 *
 * Features:
 * - setInterval(tick, 1000) managed by useEffect
 * - Runs regardless of active module (called from app shell)
 * - Drift detection: if drift > 2s, the store's tick() snaps timeRemaining to correct value
 * - Device sleep handling via visibilitychange event + immediate tick on wake
 *
 * @see Requirements 7.8, 7.9
 */

import { useEffect } from 'react';
import { usePomodoroStore } from '@/stores/pomodoroStore';

/**
 * Hook that drives the Pomodoro timer. Must be called once from the app shell
 * (or equivalent top-level component) to ensure the timer ticks across all modules.
 */
export function usePomodoroTick(): void {
  const isRunning = usePomodoroStore((s) => s.isRunning);
  const tick = usePomodoroStore((s) => s.tick);

  // ─── Primary tick interval ─────────────────────────────────────────────────
  // Runs setInterval(tick, 1000) when the timer is running.
  // The interval cleans up when the timer stops or the component unmounts.
  useEffect(() => {
    if (!isRunning) return;

    const intervalId = setInterval(() => {
      tick();
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isRunning, tick]);

  // ─── Visibility change handler (device sleep/wake detection) ───────────────
  // When the page becomes visible again (e.g., after device sleep, tab switch),
  // trigger an immediate tick to catch up. The drift detection logic in the
  // store's tick() method will snap timeRemaining to the correct value if the
  // gap exceeds 2 seconds.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const { isRunning: currentlyRunning } = usePomodoroStore.getState();
        if (currentlyRunning) {
          usePomodoroStore.getState().tick();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
}
